import React, { useState } from 'react';
import { SubmitHandler, useForm } from "react-hook-form";
import logo from "../images/preferenceslogo.png"
import background from "../images/preferencesbg.jpg"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod";
import SuccessfulSignup from './SuccessfulSignup';
import { useParams } from 'react-router-dom';


// Validation requirements for form fields using Zod
const schema = z.object({
  email: z.string().email(),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  categories: z.string().array().nonempty({
    message: "Please select at least one category"
  }), 
  optIn: z.boolean().refine((val) => val === true, "You must opt in to proceed"),
})

type FormFields = z.infer<typeof schema>

const App = () => {
  const { apiKey, formId } = useParams<string>();
  const [ formSubmitted, setFormSubmitted ] = useState(false);
  const { register, handleSubmit, setError, formState: { errors, isSubmitting } } = useForm<FormFields>({resolver: zodResolver(schema)});

  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    const formData = [
      {
        key: 1,
        value: data.categories.join(", "),
      },
      {
        key: 2,
        value: true,
      }
  ];

    const dataFormatted = {
      FirstName: data.firstName,
      LastName: data.lastName,
      Email: data.email,
      Data: formData,
    };

    // Attempts to post data once form is submitted
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/basic`, {
        method: "POST",
        headers: {
          'x-api-key': apiKey!,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataFormatted),
      });

      // console.log("Data formatted: ", dataFormatted);

      if (response.ok) {
        const text = await response.text();
        const result = text ? JSON.parse(text) : {};
        // console.log("Success! Data was posted.");
        // console.log("Result: ", result);
        setFormSubmitted(true);
      } else {
        console.error("Failed to post data");
        setError("root", {
          message: "Error submitting form"
        })
      }
      
    } catch (error) {
      setError("root", {
        message: "Error submitting form",
      })
    }
  };

  return (
    <div className='flex justify-center items-center min-h-screen' 
    style={{
      backgroundImage: `url(${background})`,
      backgroundRepeat: "no-repeat",
      backgroundSize: "cover",
      backgroundPosition: "center"
      }}>
      <div className='bg-white p-5 rounded-sm shadow-lg max-w-[95%] md:max-w-lg w-full'>
        {formSubmitted ? ( 
          <SuccessfulSignup description='You will receive an email from us to confirm your subscription.'/> ) : 
          ( 
          <>
            <img src={logo} className='mb-5' alt="Logo"/>
            <h1 className='text-4xl mb-4 text-chestertons-purple'>Connect with us</h1>
            <p className='text-sm mb-4'>
              Keep up with the latest in property trends, market insights, and exclusive opportunities. Be the first
              to receive invitations to our special events and learn about our latest offers
            </p>
            
            <form className="form flex flex-col" onSubmit={handleSubmit(onSubmit)}>
              <label className='my-1'>Please enter your email<span className='text-red-500'>*</span></label>
              <input {...register("email")} 
              type="text" placeholder='EMAIL' className='border-solid border border-black p-1.5 text-sm'
              />
              {errors.email && <div className='text-red-500'>{errors.email.message}</div>}
              
              <label className='my-1'>Please enter your first name<span className='text-red-500'>*</span></label>
              <input {...register("firstName")} 
              type="text" placeholder='FIRST NAME' className='border-solid border border-black p-1.5 text-sm'
              />
              {errors.firstName && <div className='text-red-500'>{errors.firstName.message}</div>}
              
              <label className='my-1'>Please enter your last name<span className='text-red-500'>*</span></label>
              <input {...register("lastName")} 
              type="text" placeholder='LAST NAME' className='border-solid border border-black p-1.5 text-sm'
              />
              {errors.lastName && <div className='text-red-500'>{errors.lastName.message}</div>}
              
              <label className="mt-4 mb-2">Please select all that apply:<span className='text-red-500'>*</span></label>
              <div className="flex flex-col mb-4">
                <div className='flex items-center'>
                  <input {...register("categories")} type="checkbox" value ="Homeowner" className="peer relative appearance-none w-4 h-4 border 
                              rounded-full border-black cursor-pointer checked:bg-chestertons-purple" id="homeowner-option" />
                  <label htmlFor="homeowner-option" className="ml-2">Homeowner</label>
                </div>
                <div>
                  <div className='flex items-center'>
                    <input {...register("categories")} type="checkbox" value="Landlord" className="peer relative appearance-none w-4 h-4 border 
                                rounded-full border-black cursor-pointer checked:bg-chestertons-purple" id="landlord-option" />
                    <label htmlFor="landlord-option" className="ml-2">Landlord</label>
                  </div>
                </div>
                <div>
                  <div className='flex items-center'>
                    <input {...register("categories")} type="checkbox" value="Tenant" className="peer relative appearance-none w-4 h-4 border 
                              rounded-full border-black cursor-pointer checked:bg-chestertons-purple" id="tenant-option" />
                    <label htmlFor="tenant-option" className="ml-2">Tenant</label>
                  </div>
                </div>
                <div>
                  <div className='flex items-center'>
                    <input {...register("categories")} type="checkbox" value="Buyer" className="peer relative appearance-none w-4 h-4 border 
                              rounded-full border-black cursor-pointer checked:bg-chestertons-purple" id="buyer-option" />
                    <label htmlFor="buyer-option" className="ml-2">Buyer</label>
                  </div>
                </div>
                {errors.categories && <div className='text-red-500'>Please select at least one category</div>}
              </div>
              
              <hr className='mb-5 border border-gray-700'></hr>
              <label className='mb-2'>Opt-in<span className='text-red-500'>*</span></label>
              <div className='flex items-start mb-3'>
                <input {...register("optIn")} type="checkbox" id="Opt-in" className='w-6 h-6'/>
                <label htmlFor="Opt-in" className="text-xs ml-2">
                    I agree to receive your newsletters and accept the data privacy statement. You may unsubscribe
                    at any time using the link in the email.
                </label>
              </div>
              {errors.optIn && <div className='text-red-500 mb-2'>{errors.optIn.message}</div>}
              
              <button disabled={isSubmitting} type='submit' className='bg-chestertons-purple text-white p-2 w-24 rounded-full'>
                {isSubmitting ? "Loading" : "Submit"}
              </button>
              {errors.root && <div className='text-red-500 mt-2'>{errors.root.message}</div>}
            </form>
          </>
          )}
      </div>
    </div>
  )
}

export default App;
